import React from 'react';
import { motion } from 'framer-motion';
import type { WorkbenchMaterial } from '../types';
import { CONSTANTS } from '../data.js';
import { useGameDataContext } from '../app/GameProviders';

interface Props {
  material: WorkbenchMaterial;
  onPointerDown: (e: React.PointerEvent) => void;
}

export const WorkbenchItem = React.memo(function WorkbenchItem({ material, onPointerDown }: Props) {
  const { MATERIALS } = useGameDataContext();
  const itemData = MATERIALS[material.id];

  if (!itemData) {
    throw new Error(`WorkbenchItem: MATERIALS entry missing for id=${material.id}`);
  }

  // Snap positions to integer pixels for crisp rendering
  const rx = Math.round(material.x);
  const ry = Math.round(material.y);

  return (
    <motion.div
      key={material.instanceId}
      onPointerDown={onPointerDown}
      draggable={false}
      initial={{ scale: 0.5, opacity: 0, x: rx, y: ry }}
      animate={{ scale: 1, opacity: 1, x: rx, y: ry }}
      exit={{ scale: 0, opacity: 0, transition: { duration: 0.2 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{
        position: 'absolute',
        width: CONSTANTS.WORKBENCH_ITEM_SIZE,
        height: CONSTANTS.WORKBENCH_ITEM_SIZE,
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitFontSmoothing: 'auto',
        MozOsxFontSmoothing: 'auto',
        textRendering: 'optimizeSpeed'
      }}
      className="rounded-xl border-2 bg-cyan-500/10 border-cyan-500 flex flex-col items-center justify-center p-2 cursor-grab active:cursor-grabbing z-10"
      title={itemData.name}
    >
      <span className="text-4xl mb-1">{itemData.icon}</span>
      <span className="text-sm text-center leading-tight">{itemData.name}</span>
    </motion.div>
  );
});
